<script lang="ts">
export default {
  name: 'LayoutHeader'
}
</script>
<script lang="ts" setup>
import Notification from '@/views/Notice/common/components/Notification.vue'
import TabsView from './TabsView.vue'
import SvgIcon from '@/components/Icon/SvgIcon.vue'
import { CaretBottom } from '@element-plus/icons-vue'
import { AccountInfo } from '@/api/user/model.d'
import { getUserAccountList } from '@/api/user'
import { useUserStore } from '@/store/modules/user'
import { goto } from '@/utils'
import { getAuthCache } from '@/utils/auth'
import { OUTER_TOKEN_KEY } from '@/enums/cache'
import { ElMessage } from 'element-plus'

const userStore = useUserStore()
const route = useRoute()
const props = defineProps({
  collapsed: {
    type: Boolean,
    default: false
  },
  hidden: {
    type: Boolean
  }
})

userStore.fetchUnreadNoticeCount()

function handleLogout() {
  userStore.logout()
}

const userName = computed(() => userStore.getUserInfo?.uname || '')
const emit = defineEmits(['update:collapsed'])
const toggleCollapsed = () => {
  emit('update:collapsed', !props.collapsed)
}
const isSrmUser = computed(() => userStore.getIsSrmUser)
const srmAccountInfo = computed(() => userStore.getSrmAccountInfo)

const ifShowGuideIcon = computed(() => {
  return route.meta.showGuideIcon || false
})

const manuallyShowGuide = () => {
  useUserStore().setShowGuide(true)
  // 消息中心的新手指引
  if (route.name === 'NoticeCenterList') {
    goto('NoticeCenterList', { t: +new Date().getTime() })
  } else if (route.name === 'DeclareVariety') {
    window.location.reload()
  } else {
    goto('DeclareVariety')
  }
}

const isDevMode = import.meta.env.DEV || import.meta.env.VITE_BUILD_ENV === 'staging'

/** 未读消息数量 */
const unreadCount = computed(() => {
  // 消息中心页面
  const isPageNoticeCenter = route.name === 'NoticeCenterList'
  // 是否开启了新手指引
  const isGuide = useUserStore().showGuide
  // 如果在消息中心页面开启了新手指引，则显示1，防止getUnreadNoticeCount为0导致不显示红色圆点
  if (isPageNoticeCenter && isGuide) {
    return 1
  }
  return useUserStore().getUnreadNoticeCount
})

/** 跳转消息中心 */
const handleGotoNoticeCenter = () => {
  goto('NoticeCenterList')
}
/** 切换账号 */
const loadingAccountList = ref(false)
const companyList = ref<AccountInfo[]>([])
const fetchCompanyList = async () => {
  if (loadingAccountList.value) return
  try {
    loadingAccountList.value = true
    const data = await getUserAccountList()
    companyList.value = data
  } finally {
    loadingAccountList.value = false
  }
}
const handleSwitchAccount = (account: AccountInfo) => {
  if (account.current || !account.account) return
  const data = {
    account: account.account,
    outerToken: getAuthCache(OUTER_TOKEN_KEY) as string,
    redirectUrl: window.location.origin
  }
  const auth = window.btoa(window.btoa(JSON.stringify(data)))
  const domain = import.meta.env.VITE_SRM_DOMAIN || 'https://gys.leyopharm.com/'
  const url = `${domain}switch-account-outer?auth=${auth}`
  window.location.href = url
}

/** 联络邮箱 */
const contactEmail = 'srm-support@leyopharm.com'
/** 复制联络我们 */
async function handleCopyContactUs() {
  const { copy } = useClipboard()

  try {
    await copy(contactEmail)
    ElMessage.success('复制成功')
  } catch (error) {
    console.error(error)
    ElMessage.error('复制失败')
  }
}

/** 跳转帮助中心 */
const handleGotoHelpCenter = () => {
  goto('HelpCenter')
}
</script>

<template>
  <ALayoutHeader class="px-6 flex justify-between h-[68px] bg-white" :class="{ hidden: hidden }">
    <div class="flex items-center left">
      <div class="w-6 h-6 mr-4 cursor-pointer collapsed-wrap" @click="toggleCollapsed">
        <SvgIcon :class="{ collapsed: collapsed }" name="collapsed" size="24" />
      </div>
      <TabsView />
    </div>

    <div class="flex items-center pl-2">
      <div
        v-if="isDevMode"
        class="px-4 py-1 mx-4 text-xl text-white bg-[#F12858] rounded-[10px] font-bold flex-none"
      >
        测试服环境
      </div>
      <div class="help-center-entry" @click="handleGotoHelpCenter">
        <SvgIcon name="icon-question" size="18" />
        帮助
      </div>
      <ElPopover
        placement="bottom-end"
        popper-class="guide-btn-remind-popper"
        :width="130"
        trigger="hover"
      >
        <div class="remind-wrapper">
          <div class="remind-text">点击进入新手引导</div>
        </div>
        <template #reference>
          <div class="flex flex-col justify-center">
            <Icon
              v-show="ifShowGuideIcon"
              icon="guide-remind-icon|svg"
              class="self-center cursor-pointer"
              :size="38"
              @click="manuallyShowGuide"
            />
          </div>
        </template>
      </ElPopover>
      <Notification />

      <div class="user-toolbar">
        <ElDropdown>
          <div class="flex items-center h-8 relative outline-none">
            <span v-if="unreadCount" class="badge-num absolute left-[22px] top-[-5px]">{{
              unreadCount
            }}</span>
            <SvgIcon name="default-avator" size="30px" :class="[isSrmUser ? 'mr-4' : 'mr-2.5']" />
            <div class="user-info-wrapper">
              <template v-if="isSrmUser">
                <span>{{ srmAccountInfo.supplierName }}</span>
                <div class="flex items-center gap-x-1 mt-1 text-[12px]">
                  <span>{{ userName }}</span>
                  <span>{{ srmAccountInfo.account }}</span>
                </div>
              </template>
              <template v-else>{{ userName }}</template>
            </div>
          </div>
          <template #dropdown>
            <ElDropdownMenu class="rounded">
              <ElDropdownItem
                class="hover:bg-blue-100 hover:text-blue-600"
                @click="handleGotoNoticeCenter"
              >
                消息中心
                <span v-if="unreadCount" class="badge-num ml-1">{{ unreadCount }}</span>
              </ElDropdownItem>
              <ElDropdownItem class="hover:bg-blue-100 hover:text-blue-600">
                <ElPopover
                  ref="switchAccountPopoverRef"
                  placement="left-start"
                  :show-arrow="false"
                  :width="302"
                  trigger="hover"
                  :offset="20"
                  popper-class="user-center-switch-account-popper"
                >
                  <template #reference>
                    <div class="trigger-item" @mouseenter="fetchCompanyList">切换账号</div>
                  </template>
                  <template #default>
                    <div v-loading="loadingAccountList" class="content-container">
                      <div
                        v-for="(item, index) in companyList"
                        :key="index"
                        :class="['company', { 'company--active': item.current }]"
                        @click="handleSwitchAccount(item)"
                      >
                        <div>
                          <div class="company__name">
                            {{ item.supplierName || '' }}
                          </div>
                          <div class="company__account">
                            {{ `账号：${item.account || ''}` }}
                          </div>
                        </div>
                        <Icon :icon="item.current ? 'i-check' : 'i-arrowright'" />
                      </div>
                    </div>
                  </template>
                </ElPopover>
              </ElDropdownItem>
              <ElDropdownItem class="hover:bg-blue-100 hover:text-blue-600">
                <ElPopover
                  ref="switchAccountPopoverRef"
                  placement="left-start"
                  :show-arrow="false"
                  :width="350"
                  trigger="hover"
                  :offset="20"
                  popper-class="user-center-switch-account-popper"
                >
                  <template #reference>
                    <div class="trigger-item">联络我们</div>
                  </template>
                  <template #default>
                    <div v-loading="loadingAccountList" class="content-container text-left">
                      <span>乐药SRM客服：</span>
                      <ElButton type="text" @click="handleCopyContactUs">
                        {{ contactEmail }}
                      </ElButton>
                    </div>
                  </template>
                </ElPopover>
              </ElDropdownItem>
              <ElDropdownItem class="hover:bg-blue-100 hover:text-blue-600" @click="handleLogout">
                安全退出
              </ElDropdownItem>
            </ElDropdownMenu>
          </template>
        </ElDropdown>
      </div>
    </div>
  </ALayoutHeader>
</template>

<style scoped lang="less">
.left {
  max-width: calc(94% - 300px);
}
.user-toolbar {
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 180px;
}
.collapsed-wrap {
  line-height: 24px;
  .collapsed {
    transform: rotate(180deg);
  }
}

.guide-btn-remind-popper {
  padding: 12px 0;
  border-radius: 6px;
  .remind-wrapper {
    text-align: center;
    .remind-text {
      font-size: 14px;
      color: #262626;
    }
  }
}

.help-center-entry {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 40px;
  font-size: 18px;
  line-height: 28px;
  color: var(--gaea-text-color-regular);
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    color: var(--sop-color-primary);
  }
}

.user-info-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px 0;
  color: #333;
  font-weight: 400;
  cursor: pointer;
  &:hover {
    color: var(--sop-color-primary);
  }
}

.badge-num {
  min-width: 16px;
  height: 16px;
  border-radius: 50%;
}
</style>

<style lang="scss">
@import '@/styles/var/colors.scss';
.ant-layout-header {
  background-color: $bgPrimary;
}

.user-center-switch-account-popper.sp-popover.sp-popper {
  padding: 18px 24px 12px;
  border: none;
  border-radius: 4px;
  box-shadow: 0 0 8px 0 #e2e4eb;

  .content-container {
    &.text-left {
      text-align: left;
    }
    .company {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 6px 16px;
      border-radius: 4px;
      margin: 0 -16px 6px;
      cursor: pointer;

      &__name {
        margin-bottom: 4px;
        color: #262626;
        font-size: 14px;
      }

      &__account {
        color: #999;
        font-size: 12px;
      }

      &--active {
        .company__name {
          color: var(--gaea-color-main);
        }
      }

      &:hover {
        background: #eff2ff;
      }
    }

    .expand-list-trigger {
      display: flex;
      justify-content: center;
      margin: 10px 0 6px;
      color: var(--gaea-color-main);
      font-size: 14px;
      cursor: pointer;
    }
  }
}
</style>
